import { Elements } from '@stripe/react-stripe-js';
import {
  StripeElementsOptions,
  StripeError,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import Image from 'next/image';
import { useRouter } from 'next/router';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  IAddress,
  IBankWireInformation,
  ICountry,
  ServiceType,
} from '../../models/strapi.models';
import {
  deleteStrapi,
  postStrapi,
  putStrapi,
} from '../../services/api.service';
import { logError, logInfo } from '../../services/log.service';
import {
  addCurrencySymbol,
  calculateStripeAmount,
  downloadFile,
  flatStrapiObject,
  getTranslation,
} from '../../shared/useUtils';
import { IAppOrder } from '../../store/models/store.models';
import { setIsLoading } from '../../store/slices/appSlice';
import { FormLayoutStyle } from '../../styles/components/common/layout';
import { ModalStyle } from '../../styles/components/common/ui';
import { OrderProcessPaymentStyle } from '../../styles/components/order-process';
import AddressBookCreate, {
  IAddressBookCreateFormProps,
} from '../account/adressBook/AdressBookCreateOrModify';
import {
  Button,
  Checkbox,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Toast,
} from '../common/ui';
import Message from '../common/ui/Message';
import OrderStripeCreditCardPaymentElement from './OrderStripeCreditCardPaymentElement';

export interface IOrderProcessPaymentHandle {
  validateStep: () => boolean;
  submitPayment: () => Promise<void>;
}

interface IOrderProcessPaymentProps {
  countries: ICountry[];
  billingBankWireInformation: IBankWireInformation[];
  termsLink: string;
  labels: {
    orderSummaryTitle: string;
    billingTitle: string;
    ownerAddAddressButton: string;
    billingCreditCardTabName: string;
    billingBankWireTabName: string;
    billingBankWireNotice: string;
    billingBankWireNoticeLink: string;
    billingBankWireNoticeLinkLabel: string;
    billingBankWireBeneficiaryLabel: string;
    billingBankWireBeneficiary: string;
    billingBankWireAddressLabel: string;
    billingBankWireAddress: string;
    addressSaveSuccessMessage: string;
    addressSaveErrorMessage: string;
    addressDeletePromptMessage: string;
    addressDeleteSuccessMessage: string;
    addressDeleteErrorMessage: string;
    addressEditButton: string;
    addressDeleteButton: string;
    addressesEmptyPlaceholder: string;
    billingBankWireClipboardTip: string;
    goodsAndServicesClassLabel: string;
    termsCheckboxLabel: string;
    billingPaymentMethodsTitle: string;
    addressBookCreateOrModify: {
      aliasLabel?: string;
      aliasPlaceholder: string;
      nameLabel?: string;
      namePlaceholder: string;
      addressLabel?: string;
      addressPlaceholder: string;
      postCodeLabel?: string;
      postCodePlaceholder: string;
      cityLabel?: string;
      cityPlaceholder: string;
      countryLabel?: string;
      countryPlaceholder: string;
      referenceSectionTitle: string;
      nameSectionTitle: string;
      addressSectionTitle: string;
      taxIdLabel?: string;
      taxIdPlaceholder?: string;
      title: string;
      saveButton: string;
      deleteButton: string;
    };
    formValidation: {
      requiredField: string;
      termsAndConditionsMustBeAccepted: string;
    };
  };
  multipleOrders?: IAppOrder[] | undefined;
  order?: IAppOrder;
  addresses: IAddress[];
  orderChange: (order: IAppOrder) => void;
  addressesChange: () => void; // Notifies parent that an address has been update or deleted so, get addresses once again
  handlePaymentError: (error: StripeError) => void;
  onTabChange: (tabName: string) => void;
  onSelectedBillingAddressChange?: (
    billingAddress: IAddress | undefined
  ) => void;
  indeterminate?: boolean;
}

interface IComponentState {
  isTermsAccepted: boolean;
  errors: {
    billingAddress: string | undefined;
    stripeForm: string | undefined;
    terms: string | undefined;
  };
}

const OrderProcessPayment = forwardRef(
  (props: IOrderProcessPaymentProps, ref: ForwardedRef<any>) => {
    const initialComponentState: IComponentState = {
      isTermsAccepted: false,
      errors: {
        billingAddress: undefined,
        stripeForm: undefined,
        terms: undefined,
      },
    };
    const initialStripeState = useMemo<{
      intentCreated: boolean;
      isPristine: boolean;
      isComplete: boolean;
      isEmpty: boolean;
      isBlurred: boolean;
      options: StripeElementsOptions;
    }>(
      () => ({
        intentCreated: false,
        isPristine: true,
        isComplete: false,
        isEmpty: false,
        isBlurred: false,
        options: {
          clientSecret: '',
        },
      }),
      []
    );

    const router = useRouter();
    const dispatch = useAppDispatch();
    const orderStripeElementsRef = useRef<any>();
    const user = useAppSelector((state) => state.auth.user);
    const appState = useAppSelector((state) => state.app);
    const currentCurrency = useAppSelector(
      (state) => state.app.currentCurrency
    );
    const [state, setState] = useState(initialComponentState);
    const [canUpsertAddress, setCanUpsertAddress] = useState(false);
    const [stripePromise, setStripePromise] = useState(() =>
      loadStripe(process.env.NEXT_PUBLIC_STRIPE_KEY as string)
    );
    const [stripeState, setStripeState] = useState(initialStripeState);
    const [showToastState, setShowToastState] = useState(false);
    const [showModalState, setShowModalState] = useState(false);
    const [paymentTypeSelectorState, setPaymentTypeSelector] = useState<
      'credit-card' | 'bank-wire'
    >('credit-card');
    const [order, setOrder] = useState<IAppOrder>();
    const [multipleOrders, setMultipleOrders] = useState<IAppOrder[]>();

    const bankWireInformation = useMemo(() => {
      const current = props.billingBankWireInformation.find(
        (x) => currentCurrency && x.currencyCode === currentCurrency.isoCode
      ) as IBankWireInformation;

      return {
        title: current
          ? (getTranslation(current.title, router.locale || 'en')
              .text as string)
          : '',
        bankLabel: current
          ? (getTranslation(current.bankLabel, router.locale || 'en')
              .text as string)
          : '',
        bankCodeLabel: current
          ? (getTranslation(current.bankCodeLabel, router.locale || 'en')
              .text as string)
          : '',
        accountLabel:
          current && current.accountLabel && current.accountLabel.length > 0
            ? getTranslation(current.accountLabel, router.locale || 'en').text
            : null,
        routingLabel:
          current && current.routingLabel && current.routingLabel.length > 0
            ? getTranslation(current.routingLabel, router.locale || 'en').text
            : null,
        ibanLabel:
          current && current.ibanLabel && current.ibanLabel.length > 0
            ? getTranslation(current.ibanLabel, router.locale || 'en').text
            : null,
        ukSortCodeLabel:
          current &&
          current.ukSortCodeLabel &&
          current.ukSortCodeLabel.length > 0
            ? getTranslation(current.ukSortCodeLabel, router.locale || 'en')
                .text
            : null,
        bankContent: current && current.bankContent ? current.bankContent : '',
        bankCodeContent:
          current && current.bankCodeContent ? current.bankCodeContent : '',
        accountContent:
          current && current.accountContent ? current.accountContent : null,
        routingContent:
          current && current.routingContent ? current.routingContent : null,
        ibanContent:
          current && current.ibanContent ? current.ibanContent : null,
        ukSortCodeContent:
          current && current.ukSortCodeContent
            ? current.ukSortCodeContent
            : null,
      };
    }, [currentCurrency, props.billingBankWireInformation, router.locale]);

    const getAmountLabel = useCallback(
      (price: number) => {
        return addCurrencySymbol(
          price,
          appState.currentCurrency?.isoCode || 'USD',
          appState.currentLocale || 'en'
        );
      },
      [appState.currentCurrency?.isoCode, appState.currentLocale]
    );

    const isAddressChecked = useCallback(
      (address: IAddress) => {
        if (!!order) {
          return order.billingAddressId === address.id;
        }

        if (!!multipleOrders) {
          return (
            multipleOrders.filter((x) => x.billingAddressId === address.id)
              .length > 0
          );
        }
      },
      [order, multipleOrders]
    );

    const addressItems = useMemo(() => {
      return props.addresses.map((address) => ({
        id: address.id,
        checked: isAddressChecked(address),
        alias: address.alias,
        fullAddress: `${address.name}, ${address.address}, ${
          address.city
        },${' '}
      ${address.postCode},${' '}
      ${
        getTranslation(
          (address.country as ICountry).nameTranslations,
          router.locale || 'en'
        ).text as string
      }`,
      }));
    }, [isAddressChecked, props.addresses, router.locale]);

    const toastProps = useMemo<{
      toastType: 'success' | 'danger' | 'neutral';
      toastMessage: string;
    }>(
      () => ({
        toastType: 'success',
        toastMessage: '',
      }),
      []
    );

    const addressOverlayProps = useMemo<{
      currentAddressOverlayProps: IAddressBookCreateFormProps | undefined;
      sourceItem: IAddress | undefined;
    }>(
      () => ({
        currentAddressOverlayProps: undefined,
        sourceItem: undefined,
      }),
      []
    );

    const orders = useMemo(() => {
      if (!!multipleOrders && multipleOrders.length > 0) {
        return multipleOrders;
      }

      if (!!order) {
        return [order] as IAppOrder[];
      }
    }, [multipleOrders, order]);

    const handleStripePaymentIntent = useCallback(async () => {
      // Avoid create unnecessary intents
      if (stripeState.intentCreated) {
        logInfo(['Intent already created'], 'OrderProcessPayment');
        return;
      }

      let payload: any = {
        currency: currentCurrency?.isoCode || 'USD',
        consultantName:
          user?.user_info?.assignedConsultant?.user_info?.name || '',
        userId: user?.id,
        userEmail: user?.email,
      };

      if (!!order) {
        // Ongoing order payment
        payload.ordersIds = [order.strapiOrderId];
        payload.services = [order.type?.toString()];
        payload.trademarks = [order.trademarkName];
        payload.amount = calculateStripeAmount(
          order.totalPrice || 0,
          currentCurrency
        );
      } else if (!!multipleOrders && multipleOrders.length > 0) {
        payload.ordersIds = multipleOrders.map((x) => x.strapiOrderId);
        payload.services = multipleOrders.map((x) => x.type?.toString());
        payload.trademarks = multipleOrders.map((x) => x.trademarkName);
        const amount = multipleOrders
          .map((x) => x.totalPrice)
          .reduce(
            (accumulator, currentValue) =>
              (accumulator || 0) + (currentValue || 0)
          );
        payload.amount = calculateStripeAmount(amount || 0, currentCurrency);
      }

      if (
        !!payload.ordersIds &&
        payload.ordersIds.length > 0 &&
        !!payload.amount
      ) {
        try {
          const { clientSecret } = await postStrapi<any>(
            '/payments/createStripePayment',
            payload
          );
          const currentStripeState = { ...stripeState };
          currentStripeState.options.clientSecret = clientSecret;
          currentStripeState.intentCreated = true;
          setStripeState(currentStripeState);
          logInfo(['Intent already created'], 'OrderProcessPayment');
        } catch (error) {
          logError(
            ['Error', error],
            'OrderProcessPayment → handleStripePaymentIntent'
          );
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order, multipleOrders, router.locale, currentCurrency]);

    const validateBillingAddress = useCallback(
      (billingAddressId?: number) => {
        const currentState = { ...state };

        if (!billingAddressId) {
          if (!!multipleOrders && multipleOrders.length > 0) {
            billingAddressId = multipleOrders[0]?.billingAddressId;
          } else {
            billingAddressId = order?.billingAddressId;
          }
        }

        if (!billingAddressId) {
          currentState.errors.billingAddress =
            props.labels.formValidation.requiredField;
        } else {
          currentState.errors.billingAddress = undefined;
        }
        setState(currentState);
      },
      [
        multipleOrders,
        order?.billingAddressId,
        props.labels.formValidation.requiredField,
        state,
      ]
    );

    const validateStripeForm = useCallback(() => {
      const currentStripeState = { ...stripeState };
      currentStripeState.isPristine = false;
      setStripeState(currentStripeState);
    }, [stripeState]);

    const validateTerms = useCallback(
      (isTermsAccepted?: boolean) => {
        const currentState = { ...state };

        if (isTermsAccepted === undefined) {
          isTermsAccepted = state.isTermsAccepted;
        }

        if (!isTermsAccepted) {
          currentState.errors.terms =
            props.labels.formValidation.termsAndConditionsMustBeAccepted;
        } else {
          currentState.errors.terms = undefined;
        }

        setState(currentState);
      },
      [props.labels.formValidation.termsAndConditionsMustBeAccepted, state]
    );

    const handleAddressSelected = useCallback(
      (billingAddressId: number) => {
        if (multipleOrders && multipleOrders.length > 0) {
          const currentMultipleOrders = [...(multipleOrders || [])];
          currentMultipleOrders.forEach(
            (x) => (x.billingAddressId = billingAddressId)
          );

          setMultipleOrders(currentMultipleOrders);

          // Emit
          if (props.onSelectedBillingAddressChange) {
            props.onSelectedBillingAddressChange(
              props.addresses.find((x) => x.id === billingAddressId)
            );
            validateBillingAddress(billingAddressId);
            return;
          }
        }

        if (order) {
          const currentOrder = { ...order } as IAppOrder;
          currentOrder.billingAddressId = billingAddressId;
          setOrder(currentOrder);
          if (props.orderChange) {
            props.orderChange({ ...currentOrder });
          }

          validateBillingAddress(billingAddressId);
        }
      },
      [multipleOrders, order, props, validateBillingAddress]
    );

    const upsertAddress = useCallback(async () => {
      let address: IAddress | undefined;
      const addressPayload = {
        data: {
          ...addressOverlayProps.currentAddressOverlayProps?.values,
          country: props.countries.find(
            (x) =>
              x.iso ===
              addressOverlayProps.currentAddressOverlayProps?.values.countryCode
          )?.id,
          user: user?.id,
        },
      };

      if ((addressOverlayProps.sourceItem as IAddress)?.id) {
        // Update
        try {
          address = flatStrapiObject(
            (await putStrapi(
              `/addresses/${(addressOverlayProps.sourceItem as IAddress).id}`,
              addressPayload
            )) as any
          );
        } catch (error) {
          logError(['Error', error], 'RegisterPage → registerUser');
        }
      } else {
        //Create
        try {
          address = flatStrapiObject(
            (await postStrapi(`/addresses`, addressPayload)) as any
          );
        } catch (error) {
          logError(['Error', error], 'RegisterPage → registerUser');
          throw error;
        }
      }

      logInfo(['Response', address], 'upsertAddress');
      return address;
    }, [
      addressOverlayProps.currentAddressOverlayProps?.values,
      addressOverlayProps.sourceItem,
      props.countries,
      user?.id,
    ]);

    const deleteAddress = useCallback(
      async (billingAddressId: number) => {
        try {
          const addressResponse = (await deleteStrapi(
            `/addresses/${billingAddressId}`
          )) as any;
          logInfo(
            ['Response', addressResponse],
            'AddressBookPage → handleSaveAddress'
          );
        } catch (error) {
          logError(['Error', error], 'AddressBookPage → handleDeleteAddress');
          throw error;
        }

        handleAddressSelected(billingAddressId);
      },
      [handleAddressSelected]
    );

    const handleSetCurrentAddress = useCallback(
      (sourceItem?: IAddress) => {
        const addressProps: IAddressBookCreateFormProps = {
          labels: {
            aliasLabel: props.labels.addressBookCreateOrModify.aliasLabel,
            aliasPlaceholder:
              props.labels.addressBookCreateOrModify.aliasPlaceholder,
            nameLabel: props.labels.addressBookCreateOrModify.nameLabel,
            namePlaceholder:
              props.labels.addressBookCreateOrModify.namePlaceholder,
            addressLabel: props.labels.addressBookCreateOrModify.addressLabel,
            addressPlaceholder:
              props.labels.addressBookCreateOrModify.addressPlaceholder,
            postCodeLabel: props.labels.addressBookCreateOrModify.postCodeLabel,
            postCodePlaceholder:
              props.labels.addressBookCreateOrModify.postCodePlaceholder,
            cityLabel: props.labels.addressBookCreateOrModify.cityLabel,
            cityPlaceholder:
              props.labels.addressBookCreateOrModify.cityPlaceholder,
            countryLabel: props.labels.addressBookCreateOrModify.countryLabel,
            countryPlaceholder:
              props.labels.addressBookCreateOrModify.countryPlaceholder,
            referenceSectionTitle:
              props.labels.addressBookCreateOrModify.referenceSectionTitle,
            nameSectionTitle:
              props.labels.addressBookCreateOrModify.nameSectionTitle,
            addressSectionTitle:
              props.labels.addressBookCreateOrModify.addressSectionTitle,
            taxIdLabel: props.labels.addressBookCreateOrModify.taxIdLabel,
            taxIdPlaceholder:
              props.labels.addressBookCreateOrModify.taxIdPlaceholder,
            formValidation: {
              requiredField: props.labels.formValidation.requiredField,
            },
          },
          onFormChange: (data: IAddressBookCreateFormProps) => {
            addressOverlayProps.currentAddressOverlayProps = {
              ...data,
            };

            // Validate data
            const isDataValid =
              !!addressOverlayProps.currentAddressOverlayProps &&
              !!addressOverlayProps.currentAddressOverlayProps.values &&
              !!addressOverlayProps.currentAddressOverlayProps.values.address &&
              !!addressOverlayProps.currentAddressOverlayProps.values.city &&
              !!addressOverlayProps.currentAddressOverlayProps.values
                .countryCode &&
              !!addressOverlayProps.currentAddressOverlayProps.values.name &&
              !!addressOverlayProps.currentAddressOverlayProps.values
                .postCode &&
              !!addressOverlayProps.currentAddressOverlayProps.values;

            setCanUpsertAddress(isDataValid);

            const currentOrder = { ...props.order } as IAppOrder;
            setOrder(currentOrder);
            props.orderChange({ ...currentOrder });
          },
          values: {
            name: sourceItem?.name || '',
            alias: sourceItem?.alias || '',
            address: sourceItem?.address || '',
            postCode: sourceItem?.postCode || '',
            city: sourceItem?.city || '',
            countryCode: (sourceItem?.country as ICountry)?.iso || '',
            taxId: sourceItem?.taxId || '',
          },
          countries: props.countries.map((x) => ({
            label: getTranslation(x.nameTranslations, router.locale || 'en')
              .text as string,
            value: x.iso,
          })),
        };

        addressOverlayProps.currentAddressOverlayProps = {
          ...addressProps,
        };

        addressOverlayProps.sourceItem = sourceItem;
      },
      [addressOverlayProps, props, router.locale]
    );

    const handleShowModal = useCallback(() => {
      setShowModalState(true);
    }, []);

    const handleDismissModal = useCallback(() => {
      setShowModalState(false);
    }, []);

    const handleShowToast = useCallback(
      (message: string, type: 'success' | 'danger' | 'neutral') => {
        toastProps.toastType = type;
        toastProps.toastMessage = message;
        setShowToastState(true);
      },
      [toastProps]
    );

    const handleDismissToast = useCallback(() => {
      setShowToastState(false);
    }, []);

    const handleUpsertAddress = useCallback(async () => {
      dispatch(setIsLoading(true));
      handleDismissModal();

      try {
        const result = await upsertAddress();
        props.addressesChange();
        handleShowToast(props.labels.addressSaveSuccessMessage, 'success');

        if (!!result) {
          handleAddressSelected(result.id as number);
        }
      } catch (error) {
        handleShowToast(props.labels.addressSaveErrorMessage, 'danger');
      }

      dispatch(setIsLoading(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      dispatch,
      props.labels.addressSaveSuccessMessage,
      props.labels.addressSaveErrorMessage,
      props.addresses,
    ]);

    const handleDeleteAddress = useCallback(
      async (billingAddressId: number) => {
        // TODO: replace by a custom confirmation prompt
        const confirm = window.confirm(props.labels.addressDeletePromptMessage);
        if (confirm && billingAddressId) {
          dispatch(setIsLoading(true));
          const currentOrder = { ...order } as IAppOrder;

          try {
            await deleteAddress(billingAddressId);

            if (currentOrder?.billingAddressId === billingAddressId) {
              currentOrder.billingAddressId = undefined;
              setOrder(currentOrder);
              if (props.orderChange) {
                props.orderChange({ ...currentOrder });
              }
            }

            // Emit
            if (props.onSelectedBillingAddressChange) {
              props.onSelectedBillingAddressChange(undefined);
            }

            props.addressesChange();
            handleDismissModal();
            handleShowToast(
              props.labels.addressDeleteSuccessMessage,
              'success'
            );
          } catch (error) {
            handleShowToast(props.labels.addressDeleteErrorMessage, 'danger');
          }

          dispatch(setIsLoading(false));
        }
      },
      [
        deleteAddress,
        dispatch,
        handleDismissModal,
        handleShowToast,
        order,
        props,
      ]
    );

    const handleCopyToClipboard = (content: string) => {
      navigator.clipboard.writeText(content);
      handleShowToast(content, 'success');
    };

    const handleStripeSubmit = async () => {
      return await orderStripeElementsRef.current.pay();
    };

    useImperativeHandle(ref, () => ({
      validateStep() {
        validateBillingAddress();
        validateStripeForm();
        validateTerms();

        if (paymentTypeSelectorState === 'credit-card') {
          if (!!multipleOrders && multipleOrders.length > 0) {
            return (
              !!multipleOrders.filter((x) => !!x.billingAddressId).length &&
              stripeState.isComplete &&
              state.isTermsAccepted
            );
          } else {
            return (
              !!order?.billingAddressId &&
              stripeState.isComplete &&
              state.isTermsAccepted
            );
          }
        }

        if (paymentTypeSelectorState === 'bank-wire') {
          if (!!multipleOrders && multipleOrders.length > 0) {
            return (
              !!multipleOrders.filter((x) => !!x.billingAddressId).length &&
              state.isTermsAccepted
            );
          } else {
            return !!order?.billingAddressId && state.isTermsAccepted;
          }
        }
      },
      async submitPayment() {
        return await handleStripeSubmit();
      },
    }));

    useEffect(() => {
      setOrder(props.order);
      setMultipleOrders(props.multipleOrders);
    }, [props.order, props.multipleOrders]);

    useEffect(() => {
      handleStripePaymentIntent();
    }, [handleStripePaymentIntent]);

    return (
      <>
        <div className={OrderProcessPaymentStyle['order-process-payment']}>
          <h3>{props.labels.orderSummaryTitle}</h3>

          <div className="left-padded">
            {!!orders &&
              orders.length > 0 &&
              orders.map((order, index) => (
                <div
                  key={`order-${index}`}
                  className={
                    OrderProcessPaymentStyle['order-process-payment-summary']
                  }>
                  {/* Order service type */}
                  <div style={{ minWidth: '180px' }}>
                    <p>
                      <strong>
                        {order.type === ServiceType.SEARCH &&
                          'Trademark Search'}{' '}
                        {order.type === ServiceType.APPLICATION && 'Trademark'}
                      </strong>
                    </p>
                    <p
                      className={
                        OrderProcessPaymentStyle[
                          'order-process-payment-summary-classes'
                        ]
                      }>
                      {order.trademarkClasses &&
                        order.trademarkClasses.length > 0 &&
                        `${
                          props.labels.goodsAndServicesClassLabel
                        }es: ${order.trademarkClasses
                          .map((x) => x.code)
                          .toString()}`}
                    </p>
                  </div>

                  {/* Order Trademark Name */}
                  <div>{order.trademarkName}</div>

                  {/* Order Logo */}
                  <div
                    className={
                      OrderProcessPaymentStyle[
                        'order-process-payment-summary-logo'
                      ]
                    }>
                    {order.trademarkLogo && order.trademarkLogo.length > 0 && (
                      <Image
                        quality={90}
                        src={order.trademarkLogo}
                        alt={''}
                        fill></Image>
                    )}
                  </div>

                  {/* Order countries */}
                  <div>
                    {order.trademarkCountries.map((x) => x.name).join(', ')}
                  </div>

                  {/* Order amount */}
                  <div
                    className={
                      OrderProcessPaymentStyle[
                        'order-process-payment-summary-price'
                      ]
                    }>
                    {props.indeterminate ? (
                      <>
                        <div className="loader-ellipsis-inline is-contrast"></div>
                      </>
                    ) : (
                      getAmountLabel(order.totalPrice || 0)
                    )}
                  </div>
                </div>
              ))}
          </div>

          <h3>{props.labels.billingTitle}</h3>

          <div className="left-padded">
            {addressItems.length > 0 &&
              addressItems.map((address, index) => (
                <div
                  key={`address-${address.id}-${index}`}
                  className={`${
                    OrderProcessPaymentStyle['order-process-payment-address']
                  } ${
                    state.errors.billingAddress &&
                    OrderProcessPaymentStyle['invalid']
                  }`}>
                  <input
                    type="radio"
                    name="owner-addresses"
                    id={`label-${index}`}
                    checked={
                      address.checked !== undefined ? address.checked : false
                    }
                    onChange={(ev: any) => {
                      if (ev.target.checked) {
                        handleAddressSelected(address.id as number);
                      }
                    }}
                  />
                  <label htmlFor={`label-${index}`}>
                    <div
                      className={
                        OrderProcessPaymentStyle[
                          'order-process-payment-address-label'
                        ]
                      }>
                      <p>
                        <strong>{address.alias}</strong>
                      </p>
                      <p>{address.fullAddress}</p>
                    </div>
                  </label>
                  <div
                    className={
                      OrderProcessPaymentStyle[
                        'order-process-payment-address-actions'
                      ]
                    }>
                    <Button
                      onClick={() => {
                        const addressToEdit = props.addresses.find(
                          (x) => x.id === address.id
                        );
                        handleSetCurrentAddress(addressToEdit);
                        handleShowModal();
                      }}
                      color="minimal-small">
                      {props.labels.addressEditButton}
                    </Button>
                    <Button
                      onClick={() => handleDeleteAddress(address.id as number)}
                      color="minimal-small">
                      {props.labels.addressDeleteButton}
                    </Button>
                  </div>
                </div>
              ))}

            {addressItems.length === 0 && (
              <div
                className={
                  OrderProcessPaymentStyle['order-process-owner-address-empty']
                }>
                {props.labels.addressesEmptyPlaceholder}
              </div>
            )}

            {state.errors.billingAddress && (
              <div className={FormLayoutStyle['formError']}>
                <span>{state.errors.billingAddress}</span>
              </div>
            )}

            <hr className="separator-thin" />

            <Button
              className="is-text-align-center"
              color="cta-subtle"
              onClick={() => {
                handleSetCurrentAddress();
                handleShowModal();
              }}>
              {props.labels.ownerAddAddressButton}
            </Button>
          </div>

          <h3>{props.labels.billingPaymentMethodsTitle}</h3>

          <div
            className={
              OrderProcessPaymentStyle['order-process-payment-type-selector']
            }>
            <div
              className={`${OrderProcessPaymentStyle['payment-type']} ${
                paymentTypeSelectorState === 'credit-card'
                  ? OrderProcessPaymentStyle['is-active']
                  : null
              }`}
              onClick={() => {
                setPaymentTypeSelector('credit-card');
                props.onTabChange('credit-card');
              }}>
              <div>
                <Image
                  quality={90}
                  src={'/assets/images/icons/CreditCardIcon.svg'}
                  alt={'credit-card'}
                  width={28}
                  height={28}
                />
                <p>{props.labels.billingCreditCardTabName}</p>
              </div>
            </div>
            <div
              className={`${OrderProcessPaymentStyle['payment-type']} ${
                paymentTypeSelectorState === 'bank-wire'
                  ? OrderProcessPaymentStyle['is-active']
                  : null
              }`}
              onClick={() => {
                setPaymentTypeSelector('bank-wire');
                props.onTabChange('bank-wire');
              }}>
              <div>
                <Image
                  quality={90}
                  src={'/assets/images/icons/BankWireIcon.svg'}
                  alt={'bank-wire'}
                  width={28}
                  height={28}
                />
                <p>{props.labels.billingBankWireTabName}</p>
              </div>
            </div>
          </div>

          <hr className="separator-thin" />

          {paymentTypeSelectorState === 'credit-card' &&
            stripeState.options.clientSecret !== '' && (
              <div
                className={`${
                  OrderProcessPaymentStyle[
                    'order-process-payment-type-selector-content'
                  ]
                } ${
                  !stripeState.isPristine &&
                  !stripeState.isComplete &&
                  OrderProcessPaymentStyle['invalid']
                }`}>
                <Elements stripe={stripePromise} options={stripeState.options}>
                  <OrderStripeCreditCardPaymentElement
                    ordersIds={orders?.map((x) => x.strapiOrderId) as number[]}
                    handleOnReady={() =>
                      logInfo(['Stripe Elements ready!'], 'OrderProcessPayment')
                    }
                    handleOnBlur={() => {
                      const currentStripeState = { ...stripeState };
                      currentStripeState.isPristine = false;
                      currentStripeState.isBlurred = true;
                      setStripeState(currentStripeState);
                    }}
                    handleOnChange={(
                      event: StripePaymentElementChangeEvent
                    ) => {
                      const currentStripeState = { ...stripeState };
                      currentStripeState.isComplete = event.complete;
                      currentStripeState.isEmpty = event.empty;
                      setStripeState(currentStripeState);
                    }}
                    handleOnError={props.handlePaymentError}
                    ref={orderStripeElementsRef}
                  />
                </Elements>
                {!stripeState.isPristine && !stripeState.isComplete && (
                  <div
                    className={FormLayoutStyle['formError']}
                    style={{ marginTop: '12px', marginBottom: '0px' }}>
                    <span>{props.labels.formValidation.requiredField}</span>
                  </div>
                )}
              </div>
            )}

          {paymentTypeSelectorState === 'bank-wire' && (
            <div
              className={
                OrderProcessPaymentStyle[
                  'order-process-payment-type-selector-content'
                ]
              }>
              <Message
                type="neutral"
                iconPath={'/assets/images/icons/AlertIcon.svg'}>
                <ReactMarkdown>
                  {props.labels.billingBankWireNotice}
                </ReactMarkdown>
                <Button
                  onClick={() =>
                    downloadFile(
                      props.labels.billingBankWireNoticeLink,
                      'banktransferinstructions.pdf'
                    )
                  }
                  color="in-message">
                  {props.labels.billingBankWireNoticeLinkLabel}
                </Button>
              </Message>

              <p>
                <strong>{props.labels.billingBankWireBeneficiaryLabel}</strong>{' '}
                {props.labels.billingBankWireBeneficiary}{' '}
                <span
                  className={
                    OrderProcessPaymentStyle['order-process-payment-icon']
                  }
                  title={props.labels.billingBankWireClipboardTip}
                  onClick={() =>
                    handleCopyToClipboard(
                      props.labels.billingBankWireBeneficiary
                    )
                  }>
                  <Image
                    quality={90}
                    src={'/assets/images/icons/ClipboardIcon.svg'}
                    alt={'copy'}
                    width={20}
                    height={20}
                  />
                </span>
              </p>
              <p>
                <strong>{props.labels.billingBankWireAddressLabel}</strong>{' '}
                {props.labels.billingBankWireAddress}{' '}
                <span
                  className={
                    OrderProcessPaymentStyle['order-process-payment-icon']
                  }
                  title={props.labels.billingBankWireClipboardTip}
                  onClick={() =>
                    handleCopyToClipboard(props.labels.billingBankWireAddress)
                  }>
                  <Image
                    quality={90}
                    src={'/assets/images/icons/ClipboardIcon.svg'}
                    alt={'copy'}
                    width={20}
                    height={20}
                  />
                </span>
              </p>

              <h3>{bankWireInformation.title}</h3>

              {/* Bank name */}
              <p>
                <strong>{bankWireInformation.bankLabel}</strong>{' '}
                {bankWireInformation.bankContent}{' '}
                <span
                  className={
                    OrderProcessPaymentStyle['order-process-payment-icon']
                  }
                  title={props.labels.billingBankWireClipboardTip}
                  onClick={() =>
                    handleCopyToClipboard(bankWireInformation.bankContent)
                  }>
                  <Image
                    quality={90}
                    src={'/assets/images/icons/ClipboardIcon.svg'}
                    alt={'copy'}
                    width={20}
                    height={20}
                  />
                </span>
              </p>
              {/* Bank code */}
              <p>
                <strong>{bankWireInformation.bankCodeLabel}</strong>{' '}
                {bankWireInformation.bankCodeContent}{' '}
                <span
                  className={
                    OrderProcessPaymentStyle['order-process-payment-icon']
                  }
                  title={props.labels.billingBankWireClipboardTip}
                  onClick={() =>
                    handleCopyToClipboard(bankWireInformation.bankCodeContent)
                  }>
                  <Image
                    quality={90}
                    src={'/assets/images/icons/ClipboardIcon.svg'}
                    alt={'copy'}
                    width={20}
                    height={20}
                  />
                </span>
              </p>
              {/* Bank account */}
              {bankWireInformation.accountContent && (
                <p>
                  <strong>{bankWireInformation.accountLabel}</strong>{' '}
                  {bankWireInformation.accountContent}{' '}
                  <span
                    className={
                      OrderProcessPaymentStyle['order-process-payment-icon']
                    }
                    title={props.labels.billingBankWireClipboardTip}
                    onClick={() =>
                      handleCopyToClipboard(
                        bankWireInformation.accountContent as string
                      )
                    }>
                    <Image
                      quality={90}
                      src={'/assets/images/icons/ClipboardIcon.svg'}
                      alt={'copy'}
                      width={20}
                      height={20}
                    />
                  </span>
                </p>
              )}
              {/* Bank routing */}
              {bankWireInformation.routingContent && (
                <p>
                  <strong>{bankWireInformation.routingLabel}</strong>{' '}
                  {bankWireInformation.routingContent}{' '}
                  <span
                    className={
                      OrderProcessPaymentStyle['order-process-payment-icon']
                    }
                    title={props.labels.billingBankWireClipboardTip}
                    onClick={() =>
                      handleCopyToClipboard(
                        bankWireInformation.routingContent as string
                      )
                    }>
                    <Image
                      quality={90}
                      src={'/assets/images/icons/ClipboardIcon.svg'}
                      alt={'copy'}
                      width={20}
                      height={20}
                    />
                  </span>
                </p>
              )}
              {/* Bank IBAN */}
              {bankWireInformation.ibanContent && (
                <p>
                  <strong>{bankWireInformation.ibanLabel}</strong>{' '}
                  {bankWireInformation.ibanContent}{' '}
                  <span
                    className={
                      OrderProcessPaymentStyle['order-process-payment-icon']
                    }
                    title={props.labels.billingBankWireClipboardTip}
                    onClick={() =>
                      handleCopyToClipboard(
                        bankWireInformation.ibanContent as string
                      )
                    }>
                    <Image
                      quality={90}
                      src={'/assets/images/icons/ClipboardIcon.svg'}
                      alt={'copy'}
                      width={20}
                      height={20}
                    />
                  </span>
                </p>
              )}
              {/* UK sort code */}
              {bankWireInformation.ukSortCodeContent && (
                <p>
                  <strong>{bankWireInformation.ukSortCodeLabel}</strong>{' '}
                  {bankWireInformation.ukSortCodeContent}{' '}
                  <span
                    className={
                      OrderProcessPaymentStyle['order-process-payment-icon']
                    }
                    title={props.labels.billingBankWireClipboardTip}
                    onClick={() =>
                      handleCopyToClipboard(
                        bankWireInformation.ukSortCodeContent as string
                      )
                    }>
                    <Image
                      quality={90}
                      src={'/assets/images/icons/ClipboardIcon.svg'}
                      alt={'copy'}
                      width={20}
                      height={20}
                    />
                  </span>
                </p>
              )}
            </div>
          )}

          <hr className="separator-thin" />

          <div className="is-text-align-right">
            <Checkbox
              id="terms"
              style={{ justifyContent: 'end' }}
              label={
                <span>
                  <a
                    style={{ color: '#307ee1' }}
                    target="_system"
                    href={props.termsLink}>
                    {props.labels?.termsCheckboxLabel}
                  </a>
                </span>
              }
              name="terms"
              onChange={(e: any) => {
                const currentState = { ...state };
                currentState.isTermsAccepted = e.target.checked;
                if (!currentState.isTermsAccepted) {
                  currentState.errors.terms = 'Please accept the terms';
                } else {
                  currentState.errors.terms = undefined;
                }
                setState(currentState);
              }}
              error={state.errors.terms}
            />
          </div>
        </div>

        {/* Editor modal */}
        <Modal show={showModalState}>
          <ModalHeader>
            <h3>{props.labels.addressBookCreateOrModify.title}</h3>
            <Image
              quality={90}
              src={'/assets/images/icons/CloseIcon.svg'}
              className={ModalStyle['close-icon']}
              alt="Close"
              width={32}
              height={32}
              onClick={handleDismissModal}
            />
          </ModalHeader>
          <ModalContent>
            {addressOverlayProps.currentAddressOverlayProps && (
              <AddressBookCreate
                {...addressOverlayProps.currentAddressOverlayProps}
              />
            )}
          </ModalContent>
          <ModalFooter className="buttons-toolbar">
            <Button onClick={handleUpsertAddress} disabled={!canUpsertAddress}>
              {props.labels.addressBookCreateOrModify.saveButton}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Toast */}
        <Toast
          type={toastProps.toastType}
          show={showToastState}
          onDismiss={handleDismissToast}>
          {toastProps.toastMessage}
        </Toast>
      </>
    );
  }
);

OrderProcessPayment.displayName = 'OrderProcessPayment';
export default OrderProcessPayment;
